import React, { useState } from "react";
import { FcViewDetails } from "react-icons/fc";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import { SearchIcon, CountryIcon } from "../../../../assets/Icons";
import { IoChevronDownOutline } from "react-icons/io5";
import { Button } from "../../../../components/Reusable/Button";
import ProductPackages from "../../ProductPackage";
import {
  LabelInput,
  DownloadLinkInput,
} from "../../../../components/Reusable/Layout";
import { EditGamerIcon, DeleteIcon } from "../../../../assets/Icons";
import { faq } from "../../../../assets/DemoData";
import FAQModal from "../../Modal/FAQModal";
import EditFAQModal from "../../Modal/EditFAQModal";
import { AppDispatch, RootState } from "../../../../redux/store";
import {
  deleteGamecardPlan,
  fetchGamecardPlans,
  updateGamecardPlan,
} from "../../../../features/auth/store/gamecardPlanSlice";
import Modal from "../../../../components/Reusable/Modal";
import { formatCountries } from "../../../../components/Reusable/utils";
import { buildPlanStatusFormData } from "../../statusToggleUtils";
import { StatusSwitch } from "../../../../components/Reusable/ToggleSwitch";
import {
  GamecardTableProps,
  ProductSearchFilterProps,
  GamecardDetailViewProps,
} from "./types";
import { FiEdit3 } from "react-icons/fi";
import { MdOutlineDelete } from "react-icons/md";
import { GamecardPlan } from "../../../../features/auth/store/types/game-types";

export const GamecardTable: React.FC<GamecardTableProps> = ({
  gamecardPlans,
  statusFilter,
  onGamecardPlanClick,
  onDeleteClick,
  onEditClick,
  loading,
}) => {
  const {
    pagination,
    lastPage: totalPages,
    currentPage,
  } = useSelector((state: RootState) => state.gamecardPlans);
  const dispatch = useDispatch<AppDispatch>();
  const [updatingPlanIds, setUpdatingPlanIds] = useState<number[]>([]);
  // Calculate pagination values
  const { nextPageUrl, prevPageUrl } = pagination;

  // Handle page navigation
  const goToNextPage = () => {
    if (currentPage < totalPages && nextPageUrl !== null) {
      dispatch(fetchGamecardPlans({ url: nextPageUrl }));
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1 && prevPageUrl !== null) {
      dispatch(fetchGamecardPlans({ url: prevPageUrl }));
    }
  };

  const handleStatusToggle = async (gamecardPlan: typeof gamecardPlans[number]) => {
    const nextStatus = gamecardPlan.status === "on" ? "off" : "on";

    setUpdatingPlanIds((prev) => [...prev, gamecardPlan.id]);

    try {
      await dispatch(
        updateGamecardPlan({
          id: gamecardPlan.id,
          data: buildPlanStatusFormData(gamecardPlan, nextStatus),
        }),
      ).unwrap();
      toast.success(
        `Plan ${nextStatus === "on" ? "activated" : "deactivated"} successfully`,
      );
    } catch (err: any) {
      toast.error(err?.message || "Failed to update plan status");
    } finally {
      setUpdatingPlanIds((prev) => prev.filter((id) => id !== gamecardPlan.id));
    }
  };
  return (
    <div className="flex flex-col">
      <div className="overflow-x-auto">
        <table className="w-full border border-[#FFFFFF1A] rounded-2xl overflow-hidden font-Inter cousor-pointer">
          <thead>
            <tr className="bg-gray-100 dark:bg-[#FFFFFF1A] text-gray-600 dark:text-white uppercase text-xs bp:text-sm leading-normal h-[50px]">
              <th className="px-6 text-left">Status</th>
              <th className="px-6 text-left">Provider</th>
              <th className="px-6 text-left">Sub category</th>
              <th className="px-6 text-left">Product Name</th>
              <th className="px-6 text-left">Price Currency</th>
              <th className="px-6 text-left">Price Type</th>
              <th className="px-6 text-left">Wallet Price</th>
              <th className="px-6 text-left">API Price</th>
              <th className="px-6 text-left">ATM Price</th>
              <th className="px-6 text-left">Point Bonus</th>
              <th className="px-6 text-left">Discount Offered</th>
              <th className="px-6 text-left">Source</th>
              <th className="px-6 text-left">Code</th>
              <th className="px-6 text-left">Countries</th>
              <th className="px-6 text-left">Product Image</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody className="text-gray-600 dark:text-white text-xs bp:text-sm cursor-pointer">
            {loading ? (
              <tr className="border-b border-gray-200 h-[80px]">
                <td colSpan={16} className="px-6 text-center">
                  <div className="flex items-center justify-center h-full">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                  </div>
                </td>
              </tr>
            ) : gamecardPlans.length > 0 ? (
              gamecardPlans.map((gamecardPlan) => (
                <tr
                  key={gamecardPlan.id}
                  className="border-b border-gray-200 h-[50px] "
                >
                  <td className="px-6 py-4 align-middle">
                    <StatusSwitch
                      checked={gamecardPlan.status === "on"}
                      disabled={updatingPlanIds.includes(gamecardPlan.id)}
                      onChange={() => handleStatusToggle(gamecardPlan)}
                      label={`Toggle ${gamecardPlan.name || "plan"} status`}
                    />
                  </td>
                  <td className="px-6 py-4 align-middle">
                    {gamecardPlan.category?.provider?.name ||
                      gamecardPlan.provider?.name ||
                      "-"}
                  </td>
                  <td className="px-6 py-4 align-middle">
                    {gamecardPlan.category?.name ||
                      gamecardPlan.sub_category?.name ||
                      "-"}
                  </td>
                  <td className="px-6 py-4 align-middle">
                    {gamecardPlan.name || "-"}
                  </td>
                  <td className="px-6 py-4 align-middle">
                    {gamecardPlan.price_currency?.code ||
                      gamecardPlan.price_currency?.name ||
                      gamecardPlan.price_currency?.symbol ||
                      gamecardPlan.price_currency_id ||
                      "-"}
                  </td>
                  <td className="px-6 py-4 align-middle">
                    {gamecardPlan.price_type || "-"}
                  </td>
                  <td className="px-6 py-4 align-middle capitalize">
                    {gamecardPlan.wallet_price ?? "-"}
                  </td>
                  <td className="px-6 py-4 align-middle">
                    {gamecardPlan.api_price ?? "-"}
                  </td>
                  <td className="px-6 py-4 align-middle">
                    {gamecardPlan.atm_price ?? "-"}
                  </td>
                  <td className="px-6 py-4 align-middle">
                    {gamecardPlan.point_bonus ?? "-"}
                  </td>
                  <td className="px-6 py-4 align-middle">
                    {gamecardPlan.discount ?? "-"}
                  </td>
                  <td className="px-6 py-4 align-middle">
                    {gamecardPlan.source || "-"}
                  </td>
                  <td className="px-6 py-4 align-middle">
                    {gamecardPlan.code || "-"}
                  </td>
                  <td className="px-6 py-4 align-middle">
                    {formatCountries(gamecardPlan.countries)}
                  </td>
                  <td className="px-6 py-4 align-middle">
                    <img
                      src={gamecardPlan.image_url || "/default-product.png"}
                      alt={gamecardPlan.name || "product"}
                      className="size-[40px] object-cover rounded-lg"
                    />
                  </td>
                  <td className="px-6">
                    <div className="flex items-center justify-center space-x-3">
                      <button
                        className="cursor-pointer"
                        onClick={() => onGamecardPlanClick(gamecardPlan)}
                      >
                        <FcViewDetails size="20" />
                      </button>
                      <button
                        className="text-blue-600"
                        onClick={() =>
                          onEditClick(gamecardPlan as GamecardPlan)
                        }
                      >
                        <FiEdit3 className="text-2xl" />
                      </button>
                      <button
                        className="text-red-600"
                        onClick={() => onDeleteClick(gamecardPlan.id)}
                      >
                        <MdOutlineDelete className="text-2xl" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr className="border-b border-gray-200 h-[50px]">
                <td colSpan={16} className="px-6 text-center">
                  No data available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="flex justify-between items-center border-t border-[#FFFFFF1A]">
        <button
          className={`border border-[#D0D5DD] dark:border-[#FFFFFF1A] px-4 py-2 rounded-md text-xs bp:text-sm dark:text-white dark:bg-[#FFFFFF1A] ${
            currentPage === 1 ? "opacity-50 cursor-not-allowed" : ""
          }`}
          onClick={goToPreviousPage}
          disabled={currentPage === 1}
        >
          Previous
        </button>
        <span className="text-gray-600 dark:text-white text-xs bp:text-sm">
          Page {currentPage} of {totalPages || 1}
        </span>
        <button
          className={`border border-[#D0D5DD] dark:border-[#FFFFFF1A] px-4 py-2 rounded-md text-xs bp:text-sm dark:text-white dark:bg-[#FFFFFF1A] ${
            currentPage === totalPages || totalPages === 0
              ? "opacity-50 cursor-not-allowed"
              : ""
          }`}
          onClick={goToNextPage}
          disabled={currentPage === totalPages || totalPages === 0}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export const GamecardSearchFilter: React.FC<ProductSearchFilterProps> = ({
  onStatusFilter,
  onCreateNewProduct,
  searchTerm,
  onSearchChange,
}) => {
  const [showStatusDropdown, setShowStatusDropdown] = useState<boolean>(false);
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);

  const statusOptions = ["on", "off"];

  const handleStatusSelect = (status: string) => {
    setSelectedStatus(status);
    onStatusFilter(status);
    setShowStatusDropdown(false);
  };

  const clearStatusFilter = () => {
    setSelectedStatus(null);
    onStatusFilter("");
    setShowStatusDropdown(false);
  };
  return (
    <div className="flex flex-col bp:flex-row bp:items-center bp:justify-between mb-4 gap-2">
      <div className="relative">
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search for a product..."
          className="border p-2 pl-10 rounded-3xl w-[260px] h-[40px] bp:h-[48px] bp:w-[500px] dark:text-white dark:bg-[#FFFFFF1A] bg-[#F4F4F5] dark:border-[#FFFFFF1A]"
        />
        <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
          <SearchIcon />
        </div>
      </div>
      <div className="flex flex-col md:flex-row gap-2">
        <button className="border p-2 bg-[#F4F4F5] w-[260px] h-[40px] md:w-[100px] rounded-3xl dark:text-white dark:bg-[#FFFFFF1A] dark:border-[#FFFFFF1A]">
          <div className="flex items-center justify-between gap-2">
            <CountryIcon />
            <IoChevronDownOutline />
          </div>
        </button>
        <button className="border p-2 bg-[#F4F4F5] w-[260px] h-[40px] md:w-[100px] rounded-3xl dark:text-white dark:bg-[#FFFFFF1A] dark:border-[#FFFFFF1A]">
          <div className="flex items-center gap-2 justify-between">
            <p className="text-sm">Category</p> <IoChevronDownOutline />
          </div>
        </button>
        <div className="relative">
          <button
            className="border p-2 bg-[#F4F4F5] w-[260px] h-[40px] md:w-[100px] rounded-3xl dark:text-white dark:bg-[#FFFFFF1A] dark:border-[#FFFFFF1A]"
            onClick={() => setShowStatusDropdown(!showStatusDropdown)}
          >
            <div className="flex items-center gap-2 justify-between">
              <p className="text-sm">{selectedStatus || "Status"}</p>{" "}
              <IoChevronDownOutline />
            </div>
          </button>
          {showStatusDropdown && (
            <div className="absolute   right-15 z-10 mt-1 w-[260px] md:w-[100px] bg-white dark:bg-[#1E1E1E] border border-gray-200 dark:border-[#FFFFFF1A] rounded-md shadow-lg">
              {statusOptions.map((status) => (
                <div
                  key={status}
                  className="px-4 py-2 hover:bg-gray-100 dark:hover:bg-[#FFFFFF1A] cursor-pointer"
                  onClick={() => handleStatusSelect(status)}
                >
                  {status}
                </div>
              ))}
              {selectedStatus && (
                <div
                  className="px-4 py-2 text-red-500 hover:bg-gray-100 dark:hover:bg-[#FFFFFF1A] cursor-pointer border-t"
                  onClick={clearStatusFilter}
                >
                  Clear Filter
                </div>
              )}
            </div>
          )}
        </div>
        <Button
          onClick={() => onCreateNewProduct("3")}
          bgColor="bg-gradient-to-r from-[#027EC2] to-[#039EF2]"
          textColor="text-white"
          borderRadius="rounded-lg"
          className="w-full h-10 ml-6"
        >
          New Products
        </Button>
      </div>
    </div>
  );
};

export const GamecardDetailView: React.FC<GamecardDetailViewProps> = ({
  gamecardPlan,
  onBack,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  // Controlled values
  const [name, setName] = useState(gamecardPlan.name);
  const [message, setMessage] = useState(gamecardPlan.message);
  const [status, setStatus] = useState(gamecardPlan.status);

  const [toggle, setToggle] = useState<string>("Product Details");
  const tabItems = ["Product Details", "Product Packages"];
  const [fieldSearch, setFieldSearch] = useState("");
  const [countries, setCountries] = useState<string[]>(
    (gamecardPlan.countries ?? []).map((c: any) => String(c))
  );
  const [isFAQModalOpen, setIsFAQModalOpen] = useState(false);
  const [isEditFAQModalOpen, setIsEditFAQModalOpen] = useState(false);
  const [googlePlayLink, setGooglePlayLink] = useState("");
  const [appStoreLink, setAppStoreLink] = useState("");
  if (!gamecardPlan) return null;

  const handleAddField = () => {
    const trimmed = fieldSearch.trim();
    if (trimmed && !countries.includes(trimmed)) {
      setCountries([...countries, trimmed]);
      setFieldSearch("");
    }
  };

  const handleRemoveField = (index: number) => {
    const updated = [...countries];
    updated.splice(index, 1);
    setCountries(updated);
  };

  const handleUpdate = async () => {
    try {
      setIsUpdating(true);
      await dispatch(
        updateGamecardPlan({
          id: gamecardPlan.id,
          data: {
            name,
            message,
            status,
            countries,
          },
        })
      ).unwrap();

      toast("Game card plan updated successfully!");
    } catch (error) {
      toast("Failed to update game card plan.");
      console.error("Update error:", error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      await dispatch(deleteGamecardPlan(gamecardPlan.id)).unwrap();
      toast("Game card plan deleted successfully.");
      setShowDeleteModal(false);
      onBack(); // Navigate away
      onBack(); // Go back to the previous view
    } catch (error) {
      toast("Failed to delete game card plan.");
      console.error("Delete error:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleStatusToggle = () => {
    setStatus((prev) => (prev === "on" ? "off" : "on"));
  };

  return (
    <div className="w-full font-satoshi">
      <div className="p-6">
        <div className="bg-white mb-6">
          <div className="flex items-center p-6 gap-2">
            <button
              onClick={onBack}
              className="text-gray-600 w-6 rounded-3xl bg-[#F4F4F5] dark:text-white"
            >
              ← {/* Back arrow */}
            </button>
            <h2 className="text-2xl font-semibold text-gray-800 dark:text-white">
              {gamecardPlan.name}
            </h2>
            <span
              className={`px-4 py-1 rounded-3xl text-sm ${
                gamecardPlan.status === "on"
                  ? "bg-[#ECFDF3] text-[#067647] border border-[#ABEFC6]"
                  : "bg-[#FFFAEB] text-[#B54708] border border-[#FEDF89]"
              }`}
            >
              {gamecardPlan.status === "on" ? "Active" : "Inactive"}
            </span>
          </div>
        </div>

        <div className="flex items-center justify-between mb-6 h-[90px] rounded bg-white p-6">
          <div className="mt-4 mb-6 p-4">
            {tabItems.map((item, index) => (
              <button
                key={index}
                className={`flex-1 py-[6px] w-[234px] h-[42px] px-[6px] bp:px-[10px] text-xs place-content-center ${
                  toggle === item
                    ? "inter-xs-semibold-dark rounded-lg font-inter dark:text-white bg-gray-100 dark:bg-[#FFFFFF1A]"
                    : "inter-xs-medium-toggleBG-dark dark:text-main-dark"
                }`}
                onClick={() => setToggle(item)}
              >
                {item}
              </button>
            ))}
          </div>
          <button
            onClick={handleUpdate}
            disabled={isUpdating}
            className="bg-white border border-[#D0D5DD] h-[40px] rounded text-sm font-semibold px-4 py-2 rounded-lg"
          >
            {isUpdating ? "Saving..." : "Save changes"}
          </button>
        </div>

        {toggle === "Product Details" ? (
          <>
            <div className="bg-white  flex gap-6 p-4 mb-8">
              <div className="bg-[#E6F5FE] dark:bg-[#FFFFFF0A] p-6 rounded-lg">
                <div className="flex  gap-6 justify-between items-center mb-2">
                  <span className="text-sm text-[#475467] dark:text-gray-300">
                    Total purchases of all products
                  </span>
                  <button className="text-gray-400">⋮</button>
                </div>
                <div className="text-xl font-bold text-[#101828] dark:text-white">
                  1,000
                </div>
              </div>

              <div className="bg-[#F6FEF9] dark:bg-[#FFFFFF0A] p-6 rounded-lg">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-[#475467] dark:text-gray-300">
                    Total Revenue Generated
                  </span>
                  <button className="text-gray-400">⋮</button>
                </div>
                <div className="text-xl font-bold text-[#101828] dark:text-white">
                  ₦5,000,000
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1  md:grid-cols-2 gap-8">
              <div>
                <div className="border border-gray-200 bg-white dark:border-[#FFFFFF1A] rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-6">
                    Product Details
                  </h3>

                  <div className="space-y-4">
                    <div>
                      <LabelInput
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Product Name"
                        name="Product Name"
                      />
                    </div>

                    <div>
                      <LabelInput
                        type="text"
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder="Description"
                        name="Description"
                        className="h-[135px]"
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded p-4 mt-2">
                  <div className="flex flex-col border-b border-[#D0D5DD]">
                    <p className="text-lg text-[#012030] mb-6 font-semibold">
                      Product FAQ’s
                    </p>
                    {faq.map((item) => (
                      <div
                        key={item.id}
                        className="flex justify-between items-center py-4 border-b border-[#D0D5DD]"
                      >
                        <div className="flex-1">
                          <p className="font-semibold text-xs">
                            Question title
                          </p>
                          <p className="text-[#475467] text-sm">
                            {item.question}
                          </p>
                        </div>
                        <div className="flex gap-4">
                          <button onClick={() => setIsEditFAQModalOpen(true)}>
                            <EditGamerIcon />
                          </button>
                          <button>
                            <DeleteIcon />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center justify-center text-sm font-semibold">
                    <button
                      className="flex items-center mt-2 gap-2 border border-[#D0D5DD] w-[149px] h-[36px] rounded"
                      onClick={() => setIsFAQModalOpen(true)}
                    >
                      <span className="ml-2">+</span>
                      <p>Add New FAQ</p>
                    </button>
                  </div>
                </div>
              </div>

              <div>
                <div className="border border-gray-200 bg-white dark:border-[#FFFFFF1A] rounded-lg p-6 mb-6">
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
                    Product Images
                  </h3>
                  <div className="relative">
                    <img
                      src={gamecardPlan.image_url || "/api/placeholder/400/200"}
                      alt={gamecardPlan.name}
                      className="w-full h-40 object-cover rounded-lg"
                    />
                    <div className="absolute top-2 right-2 flex gap-2">
                      <button className="bg-white dark:bg-[#1E1E1E] p-2 rounded-full">
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M18.5 2.49998C18.8978 2.10216 19.4374 1.87866 20 1.87866C20.5626 1.87866 21.1022 2.10216 21.5 2.49998C21.8978 2.89781 22.1213 3.43737 22.1213 3.99998C22.1213 4.56259 21.8978 5.10216 21.5 5.49998L12 15L8 16L9 12L18.5 2.49998Z"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                      <button className="bg-white text-red-500 hover:text-red-700 dark:bg-[#1E1E1E] p-2 rounded-full">
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M3 6H5H21"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>

                <div className="border border-gray-200 bg-white dark:border-[#FFFFFF1A] rounded-lg p-6 mb-6">
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
                    Product Availability
                  </h3>
                  <div className="flex justify-between items-center">
                    <span className="text-[#475467] text-base font-inter dark:text-gray-300">
                      Publish product
                    </span>
                    <label className="inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={status === "on"}
                        onChange={handleStatusToggle}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-500 rounded-full peer peer-checked:bg-green-500"></div>
                      <span className="ml-3 text-sm font-medium text-gray-900 dark:text-white">
                        {status === "on" ? "On" : "Off"}
                      </span>
                    </label>
                  </div>
                </div>

                <div className="bg-white border border-gray-200 dark:border-[#FFFFFF1A] rounded-lg p-6 mb-6">
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
                    Links
                  </h3>
                  <div className="p-6 space-y-3 max-w-lg">
                    <div className="text-gray-400 text-sm mb-1">
                      Download Link
                    </div>
                    <DownloadLinkInput
                      name="googlePlayLink"
                      value={googlePlayLink}
                      onChange={(e) => setGooglePlayLink(e.target.value)}
                      placeholder="Download Link"
                      iconType="google"
                    />

                    <div className="text-gray-400 text-sm mb-1">
                      Download Link
                    </div>
                    <DownloadLinkInput
                      name="appStoreLink"
                      value={appStoreLink}
                      onChange={(e) => setAppStoreLink(e.target.value)}
                      placeholder="Download Link"
                      iconType="apple"
                    />
                  </div>
                </div>

                <div className="bg-white border border-gray-200 dark:border-[#FFFFFF1A] rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
                    Countries
                  </h3>
                  <div className="flex gap-2 items-center mb-4">
                    <div className="relative w-full">
                      <input
                        type="text"
                        placeholder="Search for country"
                        value={fieldSearch}
                        onChange={(e) => setFieldSearch(e.target.value)}
                        className="w-full p-2 border rounded bg-white pl-10"
                      />
                      <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                        <SearchIcon />
                      </div>
                    </div>
                    <button
                      onClick={handleAddField}
                      className=" w-[150px] text-base font-semibold border border-[#D0D5DD] dark:border-[#FFFFFF1A] px-2 py-2 rounded-md text-sm dark:text-white dark:bg-[#FFFFFF1A]"
                    >
                      Add Country
                    </button>
                  </div>

                  <div className="flex flex-wrap gap-2 bg-white p-4 rounded">
                    {countries.map((item, idx) => (
                      <div
                        key={idx}
                        className="flex items-center gap-4 bg-[#e5f1fa] text-sm px-3 py-2 rounded-full"
                      >
                        {item}
                        <button
                          className="ml-2 text-xl text-gray-600"
                          onClick={() => handleRemoveField(idx)}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8 text-center">
              <button
                onClick={() => setShowDeleteModal(true)}
                className="text-red-500 text-base w-[152px] h-[48px] bg-[#FEF3F2] font-medium"
              >
                Delete Product
              </button>
            </div>
            <>
              <FAQModal
                isOpen={isFAQModalOpen}
                onClose={() => setIsFAQModalOpen(false)}
                onCreate={(question, answer) => {
                  // Handle the creation of new FAQ here
                  // You might want to update your faq state array
                  console.log("New FAQ:", { question, answer });
                }}
              />
            </>
            <EditFAQModal
              isOpen={isEditFAQModalOpen}
              onClose={() => setIsEditFAQModalOpen(false)}
              onCreate={(question, answer) => {
                // Handle the creation of new FAQ here
                // You might want to update your faq state array
                console.log("New FAQ:", { question, answer });
              }}
            />
            <Modal
              isOpen={showDeleteModal}
              onClose={() => setShowDeleteModal(false)}
            >
              <div className="text-center space-y-4">
                <h2 className="text-lg font-semibold text-gray-800 dark:text-white">
                  Are you sure you want to delete this product?
                </h2>
                <div className="flex justify-center gap-4">
                  <button
                    onClick={() => setShowDeleteModal(false)}
                    className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDelete}
                    className="px-4 py-2 bg-red-600 text-white rounded-md"
                    disabled={isDeleting}
                  >
                    {isDeleting ? "Deleting..." : "Yes, Delete"}
                  </button>
                </div>
              </div>
            </Modal>
          </>
        ) : (
          <>
            <ProductPackages productId={gamecardPlan.id} />
          </>
        )}
      </div>
    </div>
  );
};
